import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchProduct,
  updateProductFields,
  updateVariantFields,
  reorderProductVariants,
  pushToShopify,
} from '@/lib/api';
import { invalidateCatalogQueries } from './catalog-keys';

export function useProduct(shopifyProductId: string | number | undefined) {
  return useQuery({
    queryKey: ['product', shopifyProductId],
    queryFn: () => fetchProduct(shopifyProductId!),
    enabled: !!shopifyProductId,
    staleTime: 10_000,
  });
}

/**
 * Edit one product's fields (title, description, status, tags, type, vendor)
 * and push to Shopify. Save *is* the push — see {@link updateProductFields}.
 */
export function useUpdateProductFields() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      shopifyProductId,
      fields,
    }: {
      shopifyProductId: number;
      fields: Record<string, unknown>;
    }) => updateProductFields(shopifyProductId, fields),
    onSuccess: () => invalidateCatalogQueries(queryClient),
  });
}

/** Edit one variant's fields (price, SKU, barcode, options, taxable) + push. */
export function useUpdateVariantFields() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      shopifyVariantId,
      shopifyProductId,
      fields,
    }: {
      shopifyVariantId: number;
      shopifyProductId: number;
      fields: Record<string, unknown>;
    }) => updateVariantFields(shopifyVariantId, shopifyProductId, fields),
    onSuccess: () => invalidateCatalogQueries(queryClient),
  });
}

/** Save a product's complete variant order locally and in Shopify. */
export function useReorderProductVariants() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      shopifyProductId,
      shopifyVariantIds,
    }: {
      shopifyProductId: number;
      shopifyVariantIds: number[];
    }) => reorderProductVariants(shopifyProductId, shopifyVariantIds),
    onSuccess: () => invalidateCatalogQueries(queryClient),
  });
}

/** Re-push any product/variant edits that failed to reach Shopify earlier. */
export function usePushProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shopifyProductId: number) => pushToShopify(shopifyProductId),
    onSuccess: () => invalidateCatalogQueries(queryClient),
  });
}
